/**
 * Générateur — Automatismes : multiplication de puissances d'exposant positif
 * d'un nombre (1218) ; multiplication de puissances de même exposant positif
 * de deux nombres (1219).
 * Attendu ID : 1218 | Niveau : 3ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, lignes 744-745 :
 * « Multiplication de puissances d'exposant positif d'un nombre. » et
 * « Multiplication de puissances de même exposant positif de deux
 * nombres. »).
 *
 * ── UN FICHIER, DEUX JEUX DE CLÉS (modèle 1101/1105) ──────────────────────
 * 1218 (clés flash_* / puissance_* / sens_*) : aⁿ × aᵐ = aⁿ⁺ᵐ, résultat en UNE
 * puissance, jamais la valeur demandée. 1219 (clés meme_exposant_*, gen_1219
 * dispatch) : aⁿ × bⁿ = (a × b)ⁿ, sens direct (2³ × 5³ = 10³). Objectifs 4e
 * l. 683-684 automatisés en 3e (mise en œuvre Eduscol 2026 p. 17 : « sur des
 * exemples à valeur générique et à partir de la définition, l'élève découvre
 * les formules »).
 * Frontières : quotient, (aⁿ)ᵐ, exposant négatif, a⁰ (objectifs l. 748-749) ;
 * trois facteurs ; exposant 1 invisible et trou sur l'exposant manquant
 * (choix mode auto : non, À ARBITRER).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 3NB3
 * options 0 et 3 (base −9..9, exposants −10..10) et 3NB1 (10ⁿ) : ici les
 * exposants POSITIFS de l'intitulé, base 2..10 dont 10 (l'item DEPP).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   1218 « Série flash » : quatre lignes « 2⁵ × 2³ = 2^⬚ », une base 10.
 *   1218 « Une seule puissance » (QCM) : [2⁸ ; 2¹⁵ ; 4⁸ ; 2²] (2² < 2⁸ <
 *     4⁸ < 2¹⁵ : la clé encadrée), 4¹⁵ (tout_multiplie) en rotation.
 *   1218 « Vrai ou faux » : quatre lignes, dont « 2³ × 2² = 32 » VRAIE
 *     (la valeur n'est pas une erreur, ≤ 1 000 seulement) et « 2³ × 3⁴ =
 *     6⁷ » FAUSSE (regle_hors_champ).
 *   1219 « Série flash » : « 2³ × 5³ = ⬚³ » (trou sur la base) trois fois,
 *     « 3⁴ × 2⁴ = 6^⬚ » (trou sur l'exposant) une fois.
 *   1219 « Une seule puissance » (QCM) : [10³ ; 10⁶ ; 7³ ; 10⁹] (7³ < 10³ <
 *     10⁶ < 10⁹), 2⁶ (base_gardee) en rotation.
 *   1219 « Vrai ou faux » : quatre lignes, dont regle_hors_champ.
 *
 * INVARIANTS : 1218 : base 2..10, exposants 2..9 distincts, n + m ≤ 15,
 * jamais n × m = n + m ; 1219 : bases a ≠ b dans 2..10, (2 ; 5) un tirage
 * sur trois, exposant 2..9 différent des deux bases ; chaque égalité vraie
 * re-vérifiée EN VALEUR (BigInt, throw) ; les distracteurs distincts en
 * valeur.
 * SACRIFIÉS (fiches « la définition comme preuve ») : (2 ; 3 ; 4) dans les
 * deux ordres pour 1218 ; (2 ; 5 ; 3) dans les deux ordres pour 1219 ;
 * auto-testés par gen1218-1219.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP test de positionnement 2de 2025 q.7
 * « 10⁵ × 10³ », réussite 56,6 %) ──────────────────────────────────────────
 *   1218 : exposants_multiplies PRINCIPALE (→ 10¹⁵, 18,2 %) ;
 *          bases_multipliees (→ 100⁸, 14,5 %) ; tout_multiplie (→ 100¹⁵,
 *          9,9 %) ; exposants_soustraits (→ 10², la règle du quotient) ;
 *          regle_hors_champ (2³ × 3⁴ = 6⁷, au V/F).
 *   1219 : exposants_additionnes PRINCIPALE (→ 10⁶, la règle de 1218 à
 *          tort) ; bases_additionnees (→ 7³) ; tout_multiplie (→ 10⁹) ;
 *          base_gardee (→ 2⁶) ; regle_hors_champ.
 */
/* global BigInt */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1218;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Une seule puissance';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les exemples des fiches : 2³ × 2⁴ (même base) et 2³ × 5³ (même exposant), dans les deux ordres. */
export const SACRIFIES = { meme_base: { base: 2, exps: [3, 4] }, meme_exposant: { bases: [2, 5], exp: 3 } };
const memeEnsemble = (u, v) => u.length === v.length && [...u].sort().join() === [...v].sort().join();
const sacrifieBase = (base, n, m) => base === SACRIFIES.meme_base.base && memeEnsemble([n, m], SACRIFIES.meme_base.exps);
const sacrifieExp = (a, b, n) => n === SACRIFIES.meme_exposant.exp && memeEnsemble([a, b], SACRIFIES.meme_exposant.bases);

/** Même base : { base, n, m } ; exposants 2..9 distincts, n + m ≤ 15, hors sacrifié. */
const tirerMemeBase = ({ baseForce = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 100; essai++) {
        const base = baseForce ?? randInt(2, 10);
        const n = randInt(2, 9);
        const m = randInt(2, 9);
        // |n − m| ≥ 2 : le distracteur exposants_soustraits ne s'écrit jamais a^1
        if (Math.abs(n - m) < 2 || n + m > 15 || n * m === n + m || exclure.includes(base) || sacrifieBase(base, n, m)) continue;
        return { base, n, m };
    }
    return { base: 3, n: 2, m: 5 };
};

/** Même exposant : { a, b, n } ; bases distinctes 2..10, (2 ; 5) un tirage sur trois, exposant 2..9 différent des bases, hors sacrifié. */
const tirerMemeExposant = ({ exclure = [] } = {}) => {
    for (let essai = 0; essai < 100; essai++) {
        const [a, b] = randInt(1, 3) === 1 ? shuffle([2, 5]) : [randInt(2, 10), randInt(2, 10)];
        const n = randInt(2, 9);
        if (a === b || n === a || n === b || exclure.includes(a * b) || sacrifieExp(a, b, n)) continue;
        return { a, b, n };
    }
    return { a: 3, b: 4, n: 2 };
};

// ─── Écritures et valeurs ─────────────────────────────────────────────────

const puissTex = (base, exp) => `${base}^{${exp}}`;
const big = (base, exp) => BigInt(base) ** BigInt(exp);
/** Assert : l'égalité « aⁿ × aᵐ = aⁿ⁺ᵐ » ou « aⁿ × bⁿ = (ab)ⁿ » est vraie en valeur. */
const verifierBase = ({ base, n, m }) => { if (big(base, n) * big(base, m) !== big(base, n + m)) throw new Error(`gen_1218 : ${base}^${n} × ${base}^${m}`); };
const verifierExp = ({ a, b, n }) => { if (big(a, n) * big(b, n) !== big(a * b, n)) throw new Error(`gen_1218 : ${a}^${n} × ${b}^${n}`); };

const EXPLICATIONS = {
    exposants_multiplies: 'Même base : les exposants s\'AJOUTENT. 2⁵ × 2³, c\'est cinq 2 puis trois 2 : huit facteurs 2, donc 2⁸.',
    bases_multipliees: 'La base ne change pas : on multiplie des 2 entre eux, le résultat est encore une puissance de 2.',
    tout_multiplie: 'Ni la base ni les exposants ne se multiplient : même base, on ajoute les exposants.',
    exposants_soustraits: 'Soustraire les exposants, c\'est pour un quotient. Ici on multiplie : on ajoute.',
    exposants_additionnes: 'Même exposant : les BASES se multiplient, l\'exposant reste. 2³ × 5³ = (2 × 5)³ = 10³.',
    bases_additionnees: 'Les bases se multiplient, elles ne s\'ajoutent pas : 2³ × 5³ = 10³.',
    base_gardee: 'Les deux bases comptent : 2³ × 5³, c\'est trois paquets de 2 × 5, donc 10³.',
    regle_hors_champ: 'Ni même base ni même exposant : aucune règle ne s\'applique, on ne peut pas écrire une seule puissance.',
    regle: 'Même base : on ajoute les exposants. Même exposant : on multiplie les bases et on garde l\'exposant.',
};

// ─── 1218 : même base ─────────────────────────────────────────────────────

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        const dix = randInt(0, 3);
        for (let i = 0; i < 4; i++) items.push(tirerMemeBase({ baseForce: i === dix ? 10 : null, exclure: items.map((it) => it.base) }));
        if (new Set(items.map((it) => it.base)).size < 4) continue;
        items.forEach(verifierBase);
        const phrase = items.map(({ base, n, m }) => `$${puissTex(base, n)} \\times ${puissTex(base, m)} = ${base}^{ {{${n + m}}} }$`).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Complète.</b> Écris chaque produit en une seule puissance.</p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, exposants_multiplies: EXPLICATIONS.exposants_multiplies } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [{ base: 2, n: 5, m: 3 }, { base: 10, n: 4, m: 2 }, { base: 3, n: 2, m: 6 }, { base: 7, n: 4, m: 5 }];
    items.forEach(verifierBase);
    const payload = makeTexteTrou(items.map(({ base, n, m }) => `$${puissTex(base, n)} \\times ${puissTex(base, m)} = ${base}^{ {{${n + m}}} }$`).join('\n'), '<p><b>Complète.</b> Écris chaque produit en une seule puissance.</p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

/** Les distracteurs de même base, en écritures ; distincts de la clé et entre eux en valeur par construction (exposants ≥ 2 distincts). */
const distracteursBase = ({ base, n, m }) => [
    { code: 'exposants_multiplies', texte: puissTex(base, n * m) },
    randomChoice([true, true, false]) ? { code: 'bases_multipliees', texte: puissTex(base * base, n + m) } : { code: 'tout_multiplie', texte: puissTex(base * base, n * m) },
    { code: 'exposants_soustraits', texte: puissTex(base, Math.abs(n - m)) },
];

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const it = tirerMemeBase();
        verifierBase(it);
        const faux = distracteursBase(it);
        const bon = tex(puissTex(it.base, it.n + it.m));
        const choix = [bon, ...faux.map((f) => tex(f.texte))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${tex(`${puissTex(it.base, it.n)} \\times ${puissTex(it.base, it.m)}`)}, c'est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = { base: 3, n: 2, m: 5 };
    const bon = tex(puissTex(3, 7));
    const payload = makeQcm([bon, tex(puissTex(3, 10)), tex(puissTex(9, 7)), tex(puissTex(3, 3))], bon, `<p><b>${tex(`${puissTex(3, 2)} \\times ${puissTex(3, 5)}`)}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'exposants_multiplies' }, { code: 'bases_multipliees' }, { code: 'exposants_soustraits' }] };
    return payload;
};

/** Une ligne « aⁿ × aᵐ = … » vraie (puissance, ou valeur ≤ 1 000), ou fausse par une famille. */
const ligneVfBase = (it, vraie) => {
    const { base, n, m } = it;
    verifierBase(it);
    const gauche = `${puissTex(base, n)} \\times ${puissTex(base, m)}`;
    if (vraie) {
        const valeur = big(base, n + m);
        if (valeur <= 1000n && randomChoice([true, false])) return { ligne: tex(`${gauche} = ${valeur}`), code: null };
        return { ligne: tex(`${gauche} = ${puissTex(base, n + m)}`), code: null };
    }
    const d = randomChoice(distracteursBase(it));
    return { ligne: tex(`${gauche} = ${d.texte}`), code: d.code };
};

/** La ligne regle_hors_champ : « aⁿ × bᵐ = (ab)ⁿ⁺ᵐ », bases et exposants différents, toujours fausse (vérifiée). */
const ligneHorsChamp = () => {
    for (let essai = 0; essai < 50; essai++) {
        const a = randInt(2, 5); const b = randInt(2, 5); const n = randInt(2, 4); const m = randInt(2, 4);
        if (a === b || n === m) continue;
        if (big(a, n) * big(b, m) === big(a * b, n + m)) continue;
        return { ligne: tex(`${puissTex(a, n)} \\times ${puissTex(b, m)} = ${puissTex(a * b, n + m)}`), code: 'regle_hors_champ' };
    }
    return { ligne: tex(`${puissTex(2, 3)} \\times ${puissTex(3, 4)} = ${puissTex(6, 7)}`), code: 'regle_hors_champ' };
};

const genVfDe = (tirerItem, ligneVf, label) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        for (let i = 0; i < 3; i++) items.push(tirerItem({ exclure: items.map((it) => it.base ?? it.a * it.b) }));
        const nbVraies = randomChoice([1, 2, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const lignes = shuffle([...items.map((it, i) => ({ ...ligneVf(it, verites[i]), vraie: verites[i] })), { ...ligneHorsChamp(), vraie: false }]);
        if (new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(label));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites: lignes.map((l) => l.vraie), distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    return null;
};

const genVfBase = () => genVfDe(tirerMemeBase, ligneVfBase, L_VF)() || (() => {
    // Repli vérifié : 3² × 3⁵ = 3⁷ V ; 10³ × 10² = 10⁶ F ; 2² × 2³ = 32 V ; 2³ × 3⁴ = 6⁷ F.
    const lignes = [tex(`${puissTex(3, 2)} \\times ${puissTex(3, 5)} = ${puissTex(3, 7)}`), tex(`${puissTex(10, 3)} \\times ${puissTex(10, 2)} = ${puissTex(10, 6)}`), tex(`${puissTex(2, 2)} \\times ${puissTex(2, 3)} = 32`), tex(`${puissTex(2, 3)} \\times ${puissTex(3, 4)} = ${puissTex(6, 7)}`)];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, false, true, false], distracteurs: [{ code: 'exposants_multiplies' }, { code: 'regle_hors_champ' }] };
    return payload;
})();

// ─── 1219 : même exposant ─────────────────────────────────────────────────

const genFlashExp = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirerMemeExposant({ exclure: items.map((it) => it.a * it.b) }));
        if (new Set(items.map((it) => it.a * it.b)).size < 4) continue;
        items.forEach(verifierExp);
        const trouExposant = randInt(0, 3);
        const phrase = items.map(({ a, b, n }, i) => (i === trouExposant
            ? `$${puissTex(a, n)} \\times ${puissTex(b, n)} = ${a * b}^{ {{${n}}} }$`
            : `$${puissTex(a, n)} \\times ${puissTex(b, n)} = {{${a * b}}}^{${n}}$`)).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Complète.</b> Écris chaque produit en une seule puissance.</p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, exposants_additionnes: EXPLICATIONS.exposants_additionnes } };
        payload._meta.params = { items, trouExposant };
        return payload;
    }
    const items = [{ a: 3, b: 4, n: 2 }, { a: 2, b: 5, n: 4 }, { a: 3, b: 7, n: 5 }, { a: 2, b: 9, n: 3 }];
    items.forEach(verifierExp);
    const payload = makeTexteTrou(items.map(({ a, b, n }, i) => (i === 0 ? `$${puissTex(a, n)} \\times ${puissTex(b, n)} = ${a * b}^{ {{${n}}} }$` : `$${puissTex(a, n)} \\times ${puissTex(b, n)} = {{${a * b}}}^{${n}}$`)).join('\n'), '<p><b>Complète.</b> Écris chaque produit en une seule puissance.</p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items, trouExposant: 0 };
    return payload;
};

/** Les distracteurs de même exposant ; distincts de la clé et entre eux en valeur (a ≠ b, n ≥ 2). */
const distracteursExp = ({ a, b, n }) => [
    { code: 'exposants_additionnes', texte: puissTex(a * b, 2 * n) },
    { code: 'bases_additionnees', texte: puissTex(a + b, n) },
    randomChoice([true, true, false]) ? { code: 'tout_multiplie', texte: puissTex(a * b, n * n) } : { code: 'base_gardee', texte: puissTex(a, 2 * n) },
];

const genQcmExp = () => {
    for (let essai = 0; essai < 100; essai++) {
        const it = tirerMemeExposant();
        verifierExp(it);
        const faux = distracteursExp(it);
        const bon = tex(puissTex(it.a * it.b, it.n));
        const choix = [bon, ...faux.map((f) => tex(f.texte))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${tex(`${puissTex(it.a, it.n)} \\times ${puissTex(it.b, it.n)}`)}, c'est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const bon = tex(puissTex(12, 2));
    const payload = makeQcm([bon, tex(puissTex(12, 4)), tex(puissTex(7, 2)), tex(puissTex(3, 4))], bon, `<p><b>${tex(`${puissTex(3, 2)} \\times ${puissTex(4, 2)}`)}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { a: 3, b: 4, n: 2, distracteurs: [{ code: 'exposants_additionnes' }, { code: 'bases_additionnees' }, { code: 'base_gardee' }] };
    return payload;
};

const ligneVfExp = (it, vraie) => {
    const { a, b, n } = it;
    verifierExp(it);
    const gauche = `${puissTex(a, n)} \\times ${puissTex(b, n)}`;
    if (vraie) return { ligne: tex(`${gauche} = ${puissTex(a * b, n)}`), code: null };
    const d = randomChoice(distracteursExp(it));
    return { ligne: tex(`${gauche} = ${d.texte}`), code: d.code };
};

const genVfExp = () => genVfDe(tirerMemeExposant, ligneVfExp, L_VF)() || (() => {
    // Repli vérifié : 3² × 4² = 12² V ; 2⁴ × 5⁴ = 10⁸ F ; 3⁵ × 7⁵ = 21⁵ V ; 2³ × 3⁴ = 6⁷ F.
    const lignes = [tex(`${puissTex(3, 2)} \\times ${puissTex(4, 2)} = ${puissTex(12, 2)}`), tex(`${puissTex(2, 4)} \\times ${puissTex(5, 4)} = ${puissTex(10, 8)}`), tex(`${puissTex(3, 5)} \\times ${puissTex(7, 5)} = ${puissTex(21, 5)}`), tex(`${puissTex(2, 3)} \\times ${puissTex(3, 4)} = ${puissTex(6, 7)}`)];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, false, true, false], distracteurs: [{ code: 'exposants_additionnes' }, { code: 'regle_hors_champ' }] };
    return payload;
})();

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé et trois familles, des ÉCRITURES en une puissance ; jamais la valeur en distracteur (2³ × 2² = 32 est VRAI, il vit au Vrai/Faux)',
        'valeurs distinctes par construction (exposants ≥ 2 distincts, bases ≥ 2 distinctes) ; collision d\'écriture → nouveau tirage',
        'sacrifiés : 2³ × 2⁴ (1218) et 2³ × 5³ (1219), dans les deux ordres',
    ],
    qcm_unique_1218: {
        support: 'base 2..10 dont 10, exposants 2..9 distincts, somme ≤ 15, jamais n × m = n + m',
        familles: {
            exposants_multiplies: 'aⁿˣᵐ, la principale (DEPP 2de 2025 q.7 : 10⁵ × 10³ → 10¹⁵, 18,2 %)',
            bases_multipliees: '(a × a)ⁿ⁺ᵐ (→ 100⁸, 14,5 %), deux fois sur trois ; sinon tout_multiplie (a × a)ⁿˣᵐ (9,9 %)',
            exposants_soustraits: 'a^|n − m|, la règle du quotient : encadre la clé par le bas',
        },
    },
    qcm_unique_1219: {
        support: 'bases a ≠ b dans 2..10, (2 ; 5) un tirage sur trois, exposant 2..9 différent des bases',
        familles: {
            exposants_additionnes: '(ab)²ⁿ, la principale : la règle de 1218 appliquée à tort',
            bases_additionnees: '(a + b)ⁿ : encadre la clé par le bas',
            tout_multiplie: '(ab)ⁿˣⁿ, deux fois sur trois ; sinon base_gardee a²ⁿ',
        },
    },
    vf_tableau_vf: 'trois lignes de l\'attendu (vraie en puissance, ou en valeur ≤ 1 000 pour 1218 ; fausse par une famille) et une ligne regle_hors_champ (bases et exposants différents, toujours fausse, vérifiée en valeur)',
};

// ─── Registre et points d'entrée ──────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    puissance_qcm_unique: genQcm,
    sens_tableau_vf: genVfBase,
    meme_exposant_flash_texte_trou_saisie: genFlashExp,
    meme_exposant_qcm_unique: genQcmExp,
    meme_exposant_tableau_vf: genVfExp,
};

/** 1218 : même base. */
// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-03 (chantier groupes-differencies) ; sert aux exercices complexes du groupe Fort.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'puissance', label: L_QCM, etapes: 1, formatsList: [{ key: 'puissance_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 2, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

/** 1219 : même exposant (clés meme_exposant_*). */
export const CATEGORIES_MEME_EXPOSANT = [
    { id: 'meme_exposant_flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'meme_exposant_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme_exposant_qcm', label: L_QCM, etapes: 1, formatsList: [{ key: 'meme_exposant_qcm_unique', label: 'QCM' }] },
    { id: 'meme_exposant_sens', label: L_VF, etapes: 2, formatsList: [{ key: 'meme_exposant_tableau_vf', label: 'Vrai/Faux' }] },
];

const CLES_1218 = Object.keys(GENERATORS).filter((k) => !k.startsWith('meme_exposant_'));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = CLES_1218.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(CLES_1218);
    } else key = randomChoice(CLES_1218);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
